import { CONFIG_QUERY_KEY } from '@modules/config/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useToastStore } from '@modules/toast/store';

import type {
  IProjectSettingsLocalesSaveResponse,
  IProjectSettingsSaveAllPayload,
  IProjectSettingsSaveResponse,
} from '../types';
import {
  ADMIN_GENERAL_SETTINGS_PROJECT_PERMISSIONS,
  SETTINGS_PROJECT_CREATIVE_LOCALES_SAVE_QUERY_KEY,
  SETTINGS_PROJECT_CREATIVE_LOCALES_SAVE_URL,
  SETTINGS_PROJECT_INTERFACE_LOCALES_SAVE_QUERY_KEY,
  SETTINGS_PROJECT_INTERFACE_LOCALES_SAVE_URL,
  SETTINGS_PROJECT_SAVE_QUERY_KEY,
  SETTINGS_PROJECT_SAVE_URL,
} from '../constants';

export default function useSettingsProjectSaveAllMutation(): ReturnType<
  typeof useContextMutation<[
    IProjectSettingsSaveResponse,
    IProjectSettingsLocalesSaveResponse,
    IProjectSettingsLocalesSaveResponse,
  ], Error, IProjectSettingsSaveAllPayload>
> {
  const { fetchPost } = useRestClient();
  const { displayPresetToast } = useToastStore();

  return useContextMutation<
    [
      IProjectSettingsSaveResponse,
      IProjectSettingsLocalesSaveResponse,
      IProjectSettingsLocalesSaveResponse,
    ],
    Error,
    IProjectSettingsSaveAllPayload
  >({
    mutationKey: [SETTINGS_PROJECT_SAVE_QUERY_KEY],
    mutationFn: (payload) => Promise.all([
      fetchPost<IProjectSettingsSaveResponse>(
        SETTINGS_PROJECT_SAVE_URL,
        payload.settings,
        {
          permission: ADMIN_GENERAL_SETTINGS_PROJECT_PERMISSIONS.PROJECTSETTINGS_EDIT,
        },
      ),
      fetchPost<IProjectSettingsLocalesSaveResponse>(
        SETTINGS_PROJECT_INTERFACE_LOCALES_SAVE_URL,
        payload.interfaceLocales,
        {
          permission: ADMIN_GENERAL_SETTINGS_PROJECT_PERMISSIONS.PROJECTSETTINGS_EDIT,
        },
      ),
      fetchPost<IProjectSettingsLocalesSaveResponse>(
        SETTINGS_PROJECT_CREATIVE_LOCALES_SAVE_URL,
        payload.creativeLocales,
        {
          permission: ADMIN_GENERAL_SETTINGS_PROJECT_PERMISSIONS.PROJECTSETTINGS_EDIT,
        },
      ),
    ]),
    async onSuccess({ queryClient }) {
      await queryClient.invalidateQueries({ queryKey: [
        SETTINGS_PROJECT_SAVE_QUERY_KEY,
        SETTINGS_PROJECT_INTERFACE_LOCALES_SAVE_QUERY_KEY,
        SETTINGS_PROJECT_CREATIVE_LOCALES_SAVE_QUERY_KEY,
      ] });
      await queryClient.refetchQueries({ queryKey: [CONFIG_QUERY_KEY] });
      displayPresetToast('edited');
    },
  });
}
